/**
 * ============================================================
 * SCARVES AUTO GIFT
 * ============================================================
 *
 * Rule:
 *
 * 1 x product_type "Scarves"
 * =
 * 1 x free gift
 *
 * Gift SKU:
 * TB.C.004 LOVE
 *
 * Shopify Product ID:
 * 15507779289473
 *
 * IMPORTANT:
 * The gift is a REAL Shopify cart item.
 * Shopify manages its inventory normally.
 *
 * The price must be made FREE using a Shopify
 * automatic Buy X Get Y discount.
 * ============================================================
 */

(() => {

  'use strict';


  /**
   * ------------------------------------------------------------
   * CONFIG
   * ------------------------------------------------------------
   */

  const SCARVES_TYPE = 'scarves';

  const GIFT_SKU = 'TB.C.004 LOVE';

  const GIFT_PRODUCT_ID = 15507779289473;


  /**
   * Resolved automatically from Shopify.
   */
  let giftVariantId = null;


  /**
   * Prevent simultaneous synchronizations.
   */
  let syncInProgress = false;


  /**
   * If Shopify refuses to add the gift, for example because
   * inventory is exhausted, don't keep retrying endlessly
   * during the same page state.
   */
  let giftAddUnavailable = false;



  /**
   * ============================================================
   * SHOPIFY CART
   * ============================================================
   */


  /**
   * Get fresh cart directly from Shopify.
   */
  async function getCart() {

    const response = await fetch(
      window.Shopify.routes.root + 'cart.js',
      {
        headers: {
          'Accept': 'application/json'
        },
        cache: 'no-store'
      }
    );


    if (!response.ok) {

      throw new Error(
        'Could not load Shopify cart.'
      );

    }


    return response.json();

  }



  /**
   * ============================================================
   * GIFT PRODUCT
   * ============================================================
   */


  /**
   * Resolve the real Shopify Variant ID for:
   *
   * SKU: TB.C.004 LOVE
   *
   * We first locate the product using Shopify Predictive Search,
   * then load the real /products/{handle}.js endpoint and find
   * the exact SKU.
   *
   * This avoids hardcoding a Variant ID.
   */
  async function getGiftVariantId() {

    /*
     * Already resolved during this page load.
     */
    if (giftVariantId) {

      return giftVariantId;

    }


    const params = new URLSearchParams({

      q: GIFT_SKU,

      'resources[type]':
        'product',

      'resources[limit]':
        '10',

      'resources[options][unavailable_products]':
        'show',

      'resources[options][fields]':
        'variants.sku'

    });


    const searchUrl =
      window.Shopify.routes.root +
      'search/suggest.json?' +
      params.toString();


    const response = await fetch(
      searchUrl,
      {
        headers: {
          'Accept': 'application/json'
        },
        cache: 'no-store'
      }
    );


    if (!response.ok) {

      throw new Error(
        'Could not search for Scarves gift.'
      );

    }


    const data =
      await response.json();


    const products =
      data?.resources?.results?.products || [];


    /*
     * Use the exact Product ID supplied from Shopify Admin.
     */
    const giftProduct =
      products.find(product => {

        return (
          Number(product.id) ===
          Number(GIFT_PRODUCT_ID)
        );

      });


    if (!giftProduct) {

      throw new Error(
        'Gift product was not found. ' +
        'SKU: ' + GIFT_SKU
      );

    }


    /*
     * Predictive Search gives us the product URL.
     *
     * Load the normal Shopify Product Ajax JSON so we can
     * inspect the real variants and their SKUs.
     */
    let productUrl =
      giftProduct.url;


    if (!productUrl) {

      throw new Error(
        'Gift product URL could not be resolved.'
      );

    }


    productUrl =
      productUrl.split('?')[0];


    if (!productUrl.endsWith('.js')) {

      productUrl += '.js';

    }


    const productResponse =
      await fetch(
        productUrl,
        {
          headers: {
            'Accept': 'application/json'
          },
          cache: 'no-store'
        }
      );


    if (!productResponse.ok) {

      throw new Error(
        'Could not load gift product.'
      );

    }


    const product =
      await productResponse.json();


    const variant =
      product.variants?.find(variant => {

        return (
          String(variant.sku || '').trim() ===
          GIFT_SKU
        );

      });


    if (!variant) {

      throw new Error(
        'No variant found with SKU: ' +
        GIFT_SKU
      );

    }


    /*
     * No stock / unavailable.
     */
    if (!variant.available) {

      giftAddUnavailable = true;

      throw new Error(
        'Gift variant is currently unavailable.'
      );

    }


    giftVariantId =
      Number(variant.id);


    return giftVariantId;

  }



  /**
   * ============================================================
   * HELPERS
   * ============================================================
   */


  function isType(item, type) {

    return (
      item.product_type &&
      item.product_type
        .trim()
        .toLowerCase() === type
    );

  }


  /**
   * Identify ONLY gifts created by this script.
   *
   * This means that if the same product somehow enters
   * the cart manually, we don't touch that line.
   */
  function isScarvesGift(item) {

    return (

      Number(item.product_id) ===
        Number(GIFT_PRODUCT_ID)

      &&

      item.properties

      &&

      item.properties._scarves_gift ===
        'true'

      &&

      item.properties._scarves_gift_sku ===
        GIFT_SKU

    );

  }



  /**
   * Analyse current cart.
   */
  function getGiftState(cart) {

    let scarvesQty = 0;

    let giftQty = 0;

    const gifts = [];


    cart.items.forEach(item => {

      /*
       * Count qualifying Scarves.
       */
      if (

        isType(
          item,
          SCARVES_TYPE
        )

        &&

        !isScarvesGift(item)

      ) {

        scarvesQty +=
          Number(item.quantity || 0);

      }


      /*
       * Count gifts managed by this script.
       */
      if (
        isScarvesGift(item)
      ) {

        giftQty +=
          Number(item.quantity || 0);

        gifts.push(item);

      }

    });


    return {

      scarvesQty,
      giftQty,
      gifts

    };

  }



  /**
   * ============================================================
   * LIQUID AJAX CART
   * ============================================================
   */


  function ajaxCartAdd(body) {

    return new Promise(resolve => {

      window.liquidAjaxCart.add(
        body,
        {
          important: true,

          lastCallback: function() {

            resolve();

          }
        }
      );

    });

  }



  function ajaxCartChange(body) {

    return new Promise(resolve => {

      window.liquidAjaxCart.change(
        body,
        {
          important: true,

          lastCallback: function() {

            resolve();

          }
        }
      );

    });

  }



  /**
   * ============================================================
   * PRODUCT PAGE WIDGET
   * ============================================================
   */


  function updateGiftWidget(
    cart,
    unavailable = false
  ) {

    const widget =
      document.querySelector(
        '#scarves-auto-gift'
      );


    /*
     * Widget only exists on a Scarves product page.
     */
    if (!widget) {

      return;

    }


    const benefit =
      widget.querySelector(
        '[data-scarves-gift-benefit]'
      );


    const success =
      widget.querySelector(
        '[data-scarves-gift-success]'
      );


    const unavailableMessage =
      widget.querySelector(
        '[data-scarves-gift-unavailable]'
      );


    /*
     * Reset.
     */
    if (benefit) {
      benefit.hidden = true;
    }

    if (success) {
      success.hidden = true;
    }

    if (unavailableMessage) {
      unavailableMessage.hidden = true;
    }



    const state =
      getGiftState(cart);



    /**
     * Gift couldn't be added.
     */
    if (
      unavailable ||
      giftAddUnavailable
    ) {

      if (unavailableMessage) {

        unavailableMessage.hidden = false;

      }

      return;

    }



    /**
     * No Scarves currently in cart.
     *
     * Show the promotional message.
     */
    if (
      state.scarvesQty === 0
    ) {

      if (benefit) {

        benefit.hidden = false;

      }

      return;

    }



    /**
     * Scarves exists, but gift hasn't reached the cart yet.
     *
     * This can happen for a very short time while Ajax
     * synchronization is running.
     */
    if (
      state.giftQty === 0
    ) {

      if (benefit) {

        benefit.hidden = false;

      }

      return;

    }



    /**
     * Gift exists.
     */
    if (success) {

      success.hidden = false;

    }



    const gift =
      state.gifts[0];


    if (!gift) {

      return;

    }



    /**
     * Gift title.
     */
    const title =
      widget.querySelector(
        '[data-scarves-gift-title]'
      );


    if (title) {

      title.textContent =
        gift.product_title ||
        gift.title ||
        'Cadou LOVE';

    }



    /**
     * Gift quantity.
     */
    const quantity =
      widget.querySelector(
        '[data-scarves-gift-quantity]'
      );


    if (quantity) {

      quantity.textContent =
        state.giftQty === 1

          ? '1 produs cadou adăugat în coș'

          : state.giftQty +
            ' produse cadou adăugate în coș';

    }



    /**
     * Gift image.
     */
    const image =
      widget.querySelector(
        '[data-scarves-gift-image]'
      );


    if (image) {

      const imageUrl =
        gift.featured_image?.url ||
        gift.image ||
        '';


      if (imageUrl) {

        image.src =
          imageUrl;

        image.alt =
          gift.product_title ||
          gift.title ||
          'Cadou LOVE';

        image.hidden = false;

      } else {

        image.hidden = true;

      }

    }

  }



  /**
   * ============================================================
   * CART CORRECTION
   * ============================================================
   */


  /**
   * Determine ONE correction.
   *
   * Desired rule:
   *
   * gift quantity === scarves quantity
   */
  function findCartCorrection(cart) {

    const state =
      getGiftState(cart);



    /**
     * Need more gifts.
     *
     * We intentionally add ONE AT A TIME.
     *
     * This makes inventory handling safer.
     * If Shopify only has 2 gifts in stock but the customer
     * adds 3 scarves, the first two can still be added.
     * The third attempt fails cleanly.
     */
    if (
      state.giftQty <
      state.scarvesQty
    ) {

      return {

        action: 'add',

        quantity: 1

      };

    }



    /**
     * Too many gifts.
     */
    if (
      state.giftQty >
      state.scarvesQty
    ) {

      const excess =
        state.giftQty -
        state.scarvesQty;


      /*
       * Correct the last matching gift line.
       */
      const gift =
        state.gifts[
          state.gifts.length - 1
        ];


      if (!gift) {

        return null;

      }


      return {

        action: 'change',

        id: gift.key,

        quantity: Math.max(
          0,
          gift.quantity - excess
        )

      };

    }



    return null;

  }



  /**
   * ============================================================
   * ADD ONE REAL GIFT
   * ============================================================
   */


  async function addOneGift() {

    if (
      giftAddUnavailable
    ) {

      return false;

    }


    const variantId =
      await getGiftVariantId();



    /**
     * Cart before request.
     */
    const beforeCart =
      await getCart();


    const beforeState =
      getGiftState(beforeCart);



    await ajaxCartAdd({

      items: [

        {

          id:
            Number(variantId),

          quantity:
            1,


          /**
           * Private line item properties.
           *
           * Properties beginning with "_" can be hidden
           * by the theme but remain available in Shopify.
           */
          properties: {

            '_scarves_gift':
              'true',

            '_scarves_gift_sku':
              GIFT_SKU

          }

        }

      ]

    });



    /**
     * Verify that Shopify ACTUALLY added it.
     *
     * This is important for inventory errors.
     */
    const afterCart =
      await getCart();


    const afterState =
      getGiftState(afterCart);



    if (
      afterState.giftQty <=
      beforeState.giftQty
    ) {

      giftAddUnavailable = true;

      console.warn(
        'Scarves gift could not be added. ' +
        'The product may be out of stock.'
      );

      return false;

    }



    return true;

  }



  /**
   * ============================================================
   * MAIN SYNCHRONIZATION
   * ============================================================
   */


  async function syncScarvesGiftCart() {

    if (
      syncInProgress ||
      !window.liquidAjaxCart
    ) {

      return;

    }



    syncInProgress = true;



    try {

      /*
       * Fresh cart.
       */
      let cart =
        await getCart();



      /*
       * If the customer removed scarves, an old stock error
       * shouldn't permanently disable future attempts.
       */
      const initialState =
        getGiftState(cart);


      if (
        initialState.scarvesQty <=
        initialState.giftQty
      ) {

        giftAddUnavailable = false;

      }



      /**
       * Maximum 50 corrections.
       *
       * Normally this loop runs only once.
       * The higher limit allows larger scarf quantities while
       * still protecting against accidental infinite loops.
       */
      for (
        let i = 0;
        i < 50;
        i++
      ) {

        cart =
          await getCart();


        const correction =
          findCartCorrection(cart);



        /**
         * Cart already correct.
         */
        if (!correction) {

          updateGiftWidget(cart);

          return;

        }



        /**
         * Missing gift.
         */
        if (
          correction.action === 'add'
        ) {

          const added =
            await addOneGift();


          /*
           * Inventory / availability problem.
           */
          if (!added) {

            const currentCart =
              await getCart();


            updateGiftWidget(
              currentCart,
              true
            );


            return;

          }


          continue;

        }



        /**
         * Too many gifts.
         */
        if (
          correction.action === 'change'
        ) {

          await ajaxCartChange({

            id:
              correction.id,

            quantity:
              correction.quantity

          });


          continue;

        }

      }



      console.warn(
        'Scarves gift synchronization stopped ' +
        'after reaching the safety limit.'
      );


      const finalCart =
        await getCart();


      updateGiftWidget(
        finalCart
      );



    } catch (error) {

      console.error(
        'Scarves gift sync error:',
        error
      );


      try {

        const cart =
          await getCart();


        updateGiftWidget(
          cart,
          giftAddUnavailable
        );

      } catch (secondaryError) {

        console.error(
          'Could not refresh Scarves gift widget:',
          secondaryError
        );

      }



    } finally {

      syncInProgress = false;

    }

  }



  /**
   * ============================================================
   * INIT
   * ============================================================
   */


  async function initScarvesGiftSystem() {

    await syncScarvesGiftCart();

  }



  if (
    window.liquidAjaxCart?.init
  ) {

    initScarvesGiftSystem();

  } else {

    document.addEventListener(
      'liquid-ajax-cart:init',
      initScarvesGiftSystem,
      {
        once: true
      }
    );

  }



  /**
   * ============================================================
   * ETHEREAL / LIQUID AJAX CART EVENTS
   * ============================================================
   *
   * React to:
   *
   * - Add to cart
   * - Quantity +
   * - Quantity -
   * - Remove product
   * - Cart drawer changes
   *
   * Our own Ajax calls also generate this event, but
   * syncInProgress prevents recursive execution.
   * ============================================================
   */

  document.addEventListener(
    'liquid-ajax-cart:request-end',
    () => {

      if (
        syncInProgress
      ) {

        return;

      }


      /*
       * A previous stock error might no longer be relevant
       * after a user changes the cart.
       */
      giftAddUnavailable = false;


      syncScarvesGiftCart();

    }
  );

})();